import FingerprintJS from "@fingerprintjs/fingerprintjs-pro";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import config from "../config";

interface Props {
  children: React.ReactNode;
}

interface BrowserFingerprintContextType {
  getBrowserFingerprint: () => Promise<string | null>;
  isBrowserFingerprintLoading: boolean;
}

const BrowserFingerprintContext =
  React.createContext<BrowserFingerprintContextType>({
    getBrowserFingerprint: () => Promise.resolve(null),
    isBrowserFingerprintLoading: false,
  });

export function BrowserFingerprintProvider(props: Props) {
  const [isBrowserFingerprintLoading, setIsBrowserFingerprintLoading] =
    useState(false);
  const fpPromise = useRef<null | ReturnType<typeof FingerprintJS.load>>(null);

  useEffect(() => {
    // Initialize an agent at application startup.
    fpPromise.current = FingerprintJS.load({
      token: config.fingerprintJsToken,
    });
  }, []);

  const getBrowserFingerprint = useCallback(async (): Promise<
    string | null
  > => {
    if (fpPromise.current !== null) {
      setIsBrowserFingerprintLoading(true);

      return fpPromise.current
        .then((fp) => fp.get({ timeout: 1000 }))
        .then((result) => result.visitorId)
        .finally(() => {
          setIsBrowserFingerprintLoading(false);
        });
    }

    return Promise.resolve(null);
  }, []);

  return (
    <BrowserFingerprintContext.Provider
      value={{ getBrowserFingerprint, isBrowserFingerprintLoading }}
    >
      {props.children}
    </BrowserFingerprintContext.Provider>
  );
}

export const useBrowserFingerprint = () =>
  useContext(BrowserFingerprintContext);
